import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { FactCheck, Build, PlaylistAddCheck } from "@mui/icons-material";
import { useMaintenanceUtilities } from "../../../stores/Admin/maintenance/maintenance.queries";
import {
  useRunMaintenanceCheck,
  useRunMaintenanceRepair,
} from "../../../stores/Admin/maintenance/maintenance.mutations";
import type {
  MaintenanceCheckResultDto,
  MaintenanceUtilityDto,
} from "../../../api/maintenanceApi";

const MaintenanceUtilitiesManager: React.FC = () => {
  const { data: utilities, isLoading, isError } = useMaintenanceUtilities();
  const checkMutation = useRunMaintenanceCheck();
  const repairMutation = useRunMaintenanceRepair();

  const [results, setResults] = useState<Record<string, MaintenanceCheckResultDto>>({});
  const [runningCheckId, setRunningCheckId] = useState<string | null>(null);
  const [runningRepairId, setRunningRepairId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<MaintenanceUtilityDto | null>(null);

  const runCheck = async (id: string) => {
    setRunningCheckId(id);
    try {
      const res = await checkMutation.mutateAsync(id);
      setResults((prev) => ({ ...prev, [id]: res }));
    } catch {
      /* handled by mutation onError */
    } finally {
      setRunningCheckId(null);
    }
  };

  const runAllChecks = async () => {
    for (const utility of utilities ?? []) {
      // eslint-disable-next-line no-await-in-loop
      await runCheck(utility.id);
    }
  };

  const runRepair = async (utility: MaintenanceUtilityDto) => {
    setConfirm(null);
    setRunningRepairId(utility.id);
    try {
      await repairMutation.mutateAsync(utility.id);
      const res = await checkMutation.mutateAsync(utility.id);
      setResults((prev) => ({ ...prev, [utility.id]: res }));
    } catch {
      /* handled by mutation onError */
    } finally {
      setRunningRepairId(null);
    }
  };

  const renderStatus = (utility: MaintenanceUtilityDto) => {
    if (runningCheckId === utility.id || runningRepairId === utility.id) {
      return <CircularProgress size={18} />;
    }
    const result = results[utility.id];
    if (!result) {
      return (
        <Typography variant="body2" color="text.secondary">
          Not checked yet
        </Typography>
      );
    }
    return (
      <Tooltip
        title={
          result.sampleIds?.length
            ? `Sample IDs: ${result.sampleIds.slice(0, 10).join(", ")}${
                result.sampleIds.length > 10 ? " …" : ""
              }`
            : ""
        }
      >
        <Box>
          <Chip
            size="small"
            color={result.count === 0 ? "success" : "warning"}
            label={result.count === 0 ? "OK" : `${result.count} affected`}
          />
          <Typography variant="caption" display="block" color="text.secondary">
            {result.message}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const isBusy = runningCheckId !== null || runningRepairId !== null;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Maintenance Utilities</Typography>
          <Typography variant="body2" color="text.secondary">
            Checks and repairs data-model inconsistencies in appointments and time slots.
            Developer tooling — intentionally in English.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="info"
          startIcon={<PlaylistAddCheck />}
          onClick={runAllChecks}
          disabled={isBusy || isLoading || !utilities?.length}
        >
          Check all
        </Button>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load maintenance utilities.
        </Alert>
      )}

      <Paper>
        <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Check</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !utilities?.length ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No maintenance utilities available.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                utilities.map((utility) => {
                  const result = results[utility.id];
                  const rowBusy =
                    runningCheckId === utility.id || runningRepairId === utility.id;
                  const repairDisabled =
                    !utility.canRepair ||
                    isBusy ||
                    (result !== undefined && result.count === 0);
                  return (
                    <TableRow key={utility.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {utility.name}
                          </Typography>
                          {utility.destructive && (
                            <Chip size="small" color="error" variant="outlined" label="deletes data" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body2" color="text.secondary">
                          {utility.description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>{renderStatus(utility)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            startIcon={<FactCheck />}
                            onClick={() => runCheck(utility.id)}
                            disabled={isBusy}
                          >
                            Check
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color={utility.destructive ? "error" : "info"}
                            startIcon={
                              runningRepairId === utility.id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <Build />
                              )
                            }
                            onClick={() => setConfirm(utility)}
                            disabled={repairDisabled}
                          >
                            Repair
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>Run repair?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography gutterBottom>
              <strong>{confirm?.name}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              {confirm?.description}
            </Typography>
            {confirm?.destructive && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This repair permanently deletes records.
              </Alert>
            )}
            {confirm && results[confirm.id] && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Currently affected: {results[confirm.id].count}
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirm?.destructive ? "error" : "info"}
            onClick={() => confirm && runRepair(confirm)}
          >
            Repair
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceUtilitiesManager;
